import { Navigate, useLocation, useParams } from "react-router-dom";
import useAcademySlug from "@/lib/use-academy-slug";
import { buildStudentPath } from "@/lib/academy-auth-url";

/**
 * Host-aware legacy redirect for student-area URLs.
 *
 * - On the platform host (dwrrah.com): emits `/<slug>/<canonical>` so the
 *   `/:slug/*` tree handles the navigation.
 * - On a custom academy domain: emits `/<canonical>` (no slug — the host IS
 *   the academy).
 *
 * Interpolates `:param` segments from `useParams()` (e.g. `:productId`,
 * `:courseId`, `:sessionId`) into the canonical path. Preserves query
 * string + hash so coupon links, `?action=buy`, `?lesson=...`, etc. survive.
 *
 * Usage:
 *   <Route path="explore/:productId" element={<StudentLegacyRedirect to="shop/explore/:productId" />} />
 */
export default function StudentLegacyRedirect({ to }) {
  const params = useParams();
  const location = useLocation();
  const slug = useAcademySlug();

  let canonical = String(to || "");
  Object.entries(params).forEach(([k, v]) => {
    if (k === "slug") return; // slug is owned by buildStudentPath, never interpolated
    if (v != null) canonical = canonical.replace(`:${k}`, String(v));
  });
  if (params["*"]) {
    canonical = `${canonical.replace(/\/+$/, "")}/${params["*"]}`;
  }

  const target = `${buildStudentPath(slug, canonical)}${location.search || ""}${location.hash || ""}`;
  return <Navigate to={target} replace />;
}
